import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface EmployeeCodeSeries {
  id: string;
  department: string;
  roleKeywords: string[];
  code: string;
  label: string;
  isActive: boolean;
  example: string;
  inUse?: number;
  updatedBy?: string | null;
  updatedByName?: string;
  updatedAt?: string | null;
}

export interface CreateEmployeeCodeSeriesPayload {
  department: string;
  roleKeywords: string[];
  code: string;
  label?: string;
  isActive?: boolean;
}

export interface UpdateEmployeeCodeSeriesPayload {
  department?: string;
  roleKeywords?: string[];
  code?: string;
  label?: string;
  isActive?: boolean;
}

export function useEmployeeCodeSeries() {
  return useQuery({
    queryKey: ['employee-code-series'],
    queryFn: async (): Promise<EmployeeCodeSeries[]> => {
      const { data } = await api.get<{ data: EmployeeCodeSeries[] }>('/hr-employee-code-series');
      return data.data ?? [];
    },
  });
}

export function useEmployeeCodeSeriesMutations() {
  const queryClient = useQueryClient();

  const createSeries = useMutation({
    mutationFn: async (payload: CreateEmployeeCodeSeriesPayload) => {
      const { data } = await api.post<{ data: EmployeeCodeSeries }>('/hr-employee-code-series', payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-code-series'] });
    },
  });

  const updateSeries = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateEmployeeCodeSeriesPayload }) => {
      const { data } = await api.put<{ data: EmployeeCodeSeries }>(`/hr-employee-code-series/${id}`, payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-code-series'] });
    },
  });

  const deleteSeries = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/hr-employee-code-series/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-code-series'] });
    },
  });

  return { createSeries, updateSeries, deleteSeries };
}
